import React, { useMemo, useRef, useEffect } from 'react';
import deepEqual from 'lodash/isEqual';

import type {
    AD_UNIT_PAGE_TYPE,
    AD_UNIT_SECTION,
    AD_UNIT_SECTION_NEW,
    Targeting
} from './types';

export const AdbridgWrapper: React.FC<
    React.PropsWithChildren<{
        page?: string;
        value: {
            targeting: Targeting;
            adBridgPage?: AD_UNIT_SECTION;
            adBridgPageNew?: AD_UNIT_SECTION_NEW;
            pageType?: AD_UNIT_PAGE_TYPE;
        };
    }>
> = ({
    children,
    value: { targeting, adBridgPage, adBridgPageNew: channel, pageType }
}) => {
    const targetingRef = useRef<Targeting>(targeting);
    const signalRef = useRef<number>(0);

    /* See: https://github.com/kentcdodds/use-deep-compare-effect */
    /* We only want to run the below useEffect if the targeting has changed */
    if (!deepEqual(targeting, targetingRef.current)) {
        targetingRef.current = targeting;
        signalRef.current += 1;
    }

    const __html = useMemo(
        () => JSON.stringify(targetingRef.current),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [signalRef.current]
    );

    useEffect(() => {
        let ignore = false;
        /* on initial render the ads are usually init'd by the AdbridgInline
        script. However on client side page navigation this may not happen as
        the iife will not re-trigger. In response to this event AdbridgInline
        will resolve these conditions and init if required */
        if (!ignore && signalRef.current === 0) {
            const event = new Event('adinit');
            window.dispatchEvent(event);
        }

        /* on subsequent targeting updates trigger event */
        if (!ignore && signalRef.current !== 0) {
            const event = new Event('adrefresh');
            window.dispatchEvent(event);
        }

        return () => {
            ignore = true;
        };
    }, [__html, signalRef]);

    return (
        <>
            {children}
            <script
                id="__ZAD_TARGETING__"
                type="application/json"
                data-adbridgpage={adBridgPage}
                data-adbridgchannel={channel}
                data-adbridgpagetype={pageType}
                dangerouslySetInnerHTML={{ __html }}
            ></script>
        </>
    );
};
